import React from "react";
import PropTypes from "prop-types";
import { Form } from "@domestika-dev/prudencio/dist/atoms/Form";
import { Grid, GridItem } from "@domestika-dev/prudencio/dist/atoms/Layout";

import { FormContentItem } from "../component";
import { NO_FOCUS, MORE_CONTENT } from "../config";

export const ContentItemsList = ({
  reference,
  contentItems,
  onRemoveElement,
  onFocusElement,
  focusedElement,
  uploadedImages,
  isMobile,
  errors,
  ...rest
}) => {
  if (!contentItems || !contentItems.length) return null;

  // if we just uploaded several images, we'll focus the first one of the list
  let focusImage = -1;

  if (!focusedElement?.id && uploadedImages) {
    focusImage = contentItems.length - uploadedImages;
  }

  const checkIfItemShouldBeFocused = (id, index) => {
    const focusedItem = focusedElement?.id === id;
    const lastItem = index === contentItems.length - 1;
    const lastItemWithoutOtherFocus = !focusedElement?.id && lastItem && focusImage === -1;
    const firstOfMultipleImages = focusImage > -1 && focusImage === index;

    return focusedItem || lastItemWithoutOtherFocus || firstOfMultipleImages;
  };

  return (
    <Grid gridGap={[4, 4, 5]} marginTop={[4, 4, 5]}>
      {contentItems?.map((item, index) => {
        const { url, id, contentId, humanizedItemType: type, position } = item;

        // ignore images without url or id
        const isImage = type === "image";

        if (isImage && (!url || !id)) {
          return null;
        }

        const itemID = contentId || id;
        const shouldBeFocused = checkIfItemShouldBeFocused(itemID, index);

        const removeContentItem = event => {
          event.stopPropagation();

          onRemoveElement(itemID);

          const previousId = contentItems[index - 1]?.id || contentItems[index - 1]?.contentId;
          const nextId = contentItems[index + 1]?.id || contentItems[index + 1]?.contentId;

          let focusedId = nextId || previousId || MORE_CONTENT;

          onFocusElement({ id: focusedId });
        };

        return (
          <GridItem
            id={itemID}
            key={itemID}
            gridColumn="1 / span 4"
            gridRow={parseInt(index, 10) + 1}
            ref={shouldBeFocused ? reference : null}
            onClick={() => {
              onFocusElement({ id: itemID });
            }}
          >
            <Form.RemoveItemWrapper id={itemID} action={removeContentItem} isMobile={isMobile}>
              <FormContentItem
                id={itemID}
                key={itemID}
                item={item}
                properties={rest}
                errors={errors[position]}
              />
            </Form.RemoveItemWrapper>
          </GridItem>
        );
      })}
    </Grid>
  );
};

ContentItemsList.propTypes = {
  contentItems: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func,
  onRemoveElement: PropTypes.func,
  validate: PropTypes.func,
  onImagesLoad: PropTypes.func,
  isImageLoading: PropTypes.bool,
  onSetEmbedContent: PropTypes.func,
  isEmbedContentLoading: PropTypes.bool,
  reference: PropTypes.func.isRequired,
  onFocusElement: PropTypes.func,
  focusedElement: PropTypes.shape({
    id: PropTypes.string,
  }),
  uploadedImages: PropTypes.number,
  isMobile: PropTypes.bool,
  errors: PropTypes.object,
};

ContentItemsList.defaultProps = {
  contentItems: [],
  onRemoveElement: () => {},
  onChange: () => {},
  validate: () => {},
  onImagesLoad: () => {},
  isImageLoading: false,
  onSetEmbedContent: () => {},
  isEmbedContentLoading: false,
  onFocusElement: () => {},
  focusedElement: {
    id: NO_FOCUS,
  },
  uploadedImages: 0,
  isMobile: false,
  errors: {},
};

export default ContentItemsList;
